import { v4 as uuidv4 } from 'uuid';

// Importando o cliente supabase centralizado
import { supabase } from '../supabase';

// Importando tipos unificados
import {
  Recommendation as SharedRecommendation,
  RecommendationRequest as SharedRecommendationRequest,
  Game as SharedGame,
  UserGame,
  RecommendationAlgorithm,
  RecommendationFilters as SharedRecommendationFilters
} from '../../types/index';

// Importando a configuração
import { RECOMMENDATION_CONFIG } from './index';

// Importando serviços 
import { ContentBasedRecommender } from './contentBasedRecommender';
import { CollaborativeRecommender } from './collaborativeRecommender';
import { PopularRecommender } from './popularRecommender';
import { HybridRecommender } from './hybridRecommender';
import { UserGameService } from '../services/userGameService';
import { UserProfileService } from '../services/userProfileService';
import { RecommendationFilterBuilder } from '../services/recommendationFilterBuilder';
import { RecommendationScorer } from '../services/recommendationScorer';
import { RecommendationStorage } from '../services/recommendationStorage';
import { AlgorithmSelector } from '../services/algorithmSelector';
import { RecommendationMonitor } from '../services/recommendationMonitor';
import { CacheService } from '../services/cacheService';
import { UserInteractionDataService } from '../services/userInteractionDataService';

/**
 * Interface para compatibilidade de tipos de jogos
 */
interface GameCompatible {
  id: string;
  name: string;
  description?: string | null;
  image_url?: string | null;
  categories?: string[] | null;
  mechanics?: string[] | null;
  min_players?: number | null;
  max_players?: number | null;
  play_time?: number | null; // Compatível com min_playtime/max_playtime
  min_playtime?: number | null;
  max_playtime?: number | null;
  complexity?: number | null;
  average_rating?: number | null;
  bgg_id?: number | null;
  year?: number | null | undefined;
  themes?: string[] | null;
  designers?: string[] | null;
  artists?: string[] | null;
  [key: string]: any; // Para campos adicionais
}

/**
 * Sistema de recomendação adaptativo
 */
export class AdaptiveRecommender {
  private contentBasedRecommender: ContentBasedRecommender;
  private collaborativeRecommender: CollaborativeRecommender;
  private popularRecommender: PopularRecommender;
  private hybridRecommender: HybridRecommender;
  private userGameService: UserGameService; 
  private userProfileService: UserProfileService;
  private filterBuilder: RecommendationFilterBuilder;
  private scorer: RecommendationScorer;
  private storage: RecommendationStorage;
  private algorithmSelector: AlgorithmSelector;
  private monitor: RecommendationMonitor;
  private supabase: typeof supabase;
  
  // Adicionar CacheService e UserInteractionDataService como propriedades
  private cacheService: CacheService;
  private userInteractionDataService: UserInteractionDataService;

  constructor(
    contentBasedRecommender?: ContentBasedRecommender,
    collaborativeRecommender?: CollaborativeRecommender,
    popularRecommender?: PopularRecommender,
    hybridRecommender?: HybridRecommender,
    userGameService?: UserGameService,
    userProfileService?: UserProfileService,
    filterBuilder?: RecommendationFilterBuilder,
    scorer?: RecommendationScorer,
    storage?: RecommendationStorage,
    algorithmSelector?: AlgorithmSelector,
    monitor?: RecommendationMonitor,
    cacheService?: CacheService, // Adicionar parâmetro opcional
    userInteractionDataService?: UserInteractionDataService // Adicionar parâmetro opcional
  ) {
    this.contentBasedRecommender = contentBasedRecommender || new ContentBasedRecommender();
    this.collaborativeRecommender = collaborativeRecommender || new CollaborativeRecommender();
    this.popularRecommender = popularRecommender || new PopularRecommender();
    this.hybridRecommender = hybridRecommender || new HybridRecommender();
    this.userProfileService = userProfileService || new UserProfileService();
    this.filterBuilder = filterBuilder || new RecommendationFilterBuilder();
    this.scorer = scorer || new RecommendationScorer();
    this.storage = storage || new RecommendationStorage();
    this.algorithmSelector = algorithmSelector || new AlgorithmSelector();
    this.monitor = monitor || new RecommendationMonitor();
    
    // Instanciar CacheService e UserInteractionDataService se não fornecidos
    this.cacheService = cacheService || new CacheService();
    this.userInteractionDataService = userInteractionDataService || new UserInteractionDataService(this.cacheService);

    // Agora UserGameService pode ser instanciado com sua dependência
    this.userGameService = userGameService || new UserGameService(this.userInteractionDataService);
    
    // Inicializar cliente supabase com a instância centralizada
    this.supabase = supabase;
  }
  
  /**
   * Gera recomendações de jogos específicas
   * (Antiga implementação do método getRecommendations do IRecommender)
   */
  public async getGameRecommendations(
    userId: string,
    count: number,
    excludeGameIds: string[],
    filters?: SharedRecommendationFilters
  ): Promise<GameCompatible[]> {
    // Criamos uma requisição para usar o método generateRecommendations
    const request: SharedRecommendationRequest = {
      userId,
      limit: count,
      dislikedGameIds: excludeGameIds,
      filters: filters
    };

    // Obtemos as recomendações no formato SharedRecommendation
    const sharedRecommendations = await this.generateRecommendations(request);
    
    // Extraímos apenas os jogos das recomendações
    return sharedRecommendations
      .map(rec => rec.game)
      .filter((game): game is SharedGame => game !== undefined)
      .map(game => ({
        id: game.id,
        name: game.name,
        description: game.description,
        image_url: game.image_url,
        bgg_id: game.bgg_id,
        average_rating: game.average_rating,
        categories: game.categories,
        mechanics: game.mechanics,
        min_players: game.min_players,
        max_players: game.max_players,
        play_time: game.play_time,
        min_playtime: game.play_time,
        max_playtime: game.play_time,
        complexity: game.complexity,
        year: game.year
      }));
  }
  
  /**
   * Método principal para gerar recomendações
   * Aceita requisições nos formatos lib ou shared, e retorna no formato shared
   */
  public async generateRecommendations(
    request: SharedRecommendationRequest
  ): Promise<SharedRecommendation[]> {
    try {
      const userId = request.userId;
      const limit = request.limit || 10;
      const dislikedGameIds = request.dislikedGameIds || [];
      const forcePersonalized = request.forcePersonalized || false;
      const filters = request.filters || {};
      
      // Determinar algoritmo a ser usado
      let algorithm = await this.algorithmSelector.determineAlgorithm(userId, forcePersonalized);
      
      // Verificar se temos dados suficientes para recomendação personalizada
      let userCollection: UserGame[] = await this.userGameService.getUserCollection(userId);
      
      // Para algoritmos personalizados, precisamos de jogos na coleção
      if (algorithm !== RecommendationAlgorithm.POPULAR && userCollection.length < RECOMMENDATION_CONFIG.selectorParams.minCollectionSizeForPersonalized) {
        // Fallback para popular se não tivermos dados suficientes
        console.log(`[AdaptiveRecommender] Usuário ${userId} tem apenas ${userCollection.length} jogos na coleção (menos que ${RECOMMENDATION_CONFIG.selectorParams.minCollectionSizeForPersonalized}). Fazendo fallback para POPULAR.`);
        algorithm = RecommendationAlgorithm.POPULAR;
      }
      
      const likedGameIds = await this.userGameService.getLikedGameIds(userId);
      
      // Adiciona metadados à recomendação
      const result: SharedRecommendation[] = [];
      
      // Definir algoritmo baseado na escolha
      let selectedAlgorithm = RecommendationAlgorithm.POPULAR;
      if (algorithm === RecommendationAlgorithm.COLLABORATIVE) {
        selectedAlgorithm = RecommendationAlgorithm.COLLABORATIVE;
      } else if (algorithm === RecommendationAlgorithm.CONTENT_BASED) {
        selectedAlgorithm = RecommendationAlgorithm.CONTENT_BASED;
      } else if (algorithm === RecommendationAlgorithm.HYBRID) {
        selectedAlgorithm = RecommendationAlgorithm.HYBRID;
      }
      
      let games: SharedGame[] = [];
      
      // Escolher qual recomendador usar com base no algoritmo
      if (algorithm === RecommendationAlgorithm.COLLABORATIVE) {
        games = await this.collaborativeRecommender.getRecommendations(
          userId, limit, dislikedGameIds, filters
        );
      } else if (algorithm === RecommendationAlgorithm.CONTENT_BASED) {
        games = await this.contentBasedRecommender.getRecommendations(
          userId, limit, dislikedGameIds, filters
        );
      } else if (algorithm === RecommendationAlgorithm.HYBRID) {
        games = await this.hybridRecommender.getRecommendations(
          userId, limit, dislikedGameIds, filters
        );
      } else {
        // Usar recomendações populares como fallback
        games = await this.popularRecommender.getRecommendations(
          userId, limit, dislikedGameIds, filters
        );
      }
      
      // Adicionar recomendações para cada jogo retornado
      for (const game of games) {
        // Calcular pontuação/motivo para o jogo, passando o algoritmo
        // Os embeddings serão buscados internamente pelo scorer
        const { score, reason } = await this.scorer.scoreGameForUser(
          userId,
          game,
          algorithm,
          userCollection
        );
        
        const recommendation: SharedRecommendation = {
          id: uuidv4(),
          user_id: userId,
          game_id: game.id,
          game: game,
          score: score,
          reason: reason || this.generateReasonText(game, algorithm),
          algorithm: selectedAlgorithm,
          viewed: false,
          liked: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        result.push(recommendation);
      }
      
      return result.slice(0, limit);
    } catch (error) {
      console.error('Erro ao gerar recomendações:', error);
      return [];
    }
  }
  
  /**
   * Gera texto explicativo para a recomendação baseado no algoritmo
   */
  private generateReasonText(game: GameCompatible, algorithm: RecommendationAlgorithm): string {
    const gameName = game.name;
    const rating = game.average_rating ? game.average_rating.toFixed(1) : "desconhecida";
    
    switch (algorithm) {
      case RecommendationAlgorithm.COLLABORATIVE:
        return `Usuários com gostos similares aos seus gostaram de ${gameName}`;
      
      case RecommendationAlgorithm.CONTENT_BASED:
        let categories = "";
        if (game.categories && game.categories.length > 0) {
          categories = game.categories.slice(0, 2).join(", ");
          return `${gameName} possui categorias similares a outros jogos que você gosta: ${categories}`;
        }
        return `${gameName} possui características similares a outros jogos que você gosta`;
      
      case RecommendationAlgorithm.HYBRID:
        return `Nosso sistema de recomendação avançado sugere ${gameName} com base no seu perfil`;
      
      case RecommendationAlgorithm.POPULAR:
      default:
        return `${gameName} é um jogo popular com avaliação ${rating}/10 na comunidade`;
    }
  }
  
  /**
   * Marca uma recomendação como visualizada
   */
  public async markRecommendationAsViewed(recommendationId: string): Promise<void> {
    await this.storage.markRecommendationAsViewed(recommendationId);
  }
  
  /**
   * Dá feedback para uma recomendação (curtiu ou não)
   */
  public async giveRecommendationFeedback(
    recommendationId: string, 
    liked: boolean | null, 
    recommendation?: SharedRecommendation
  ): Promise<SharedRecommendation> {
    // Atualizar feedback no armazenamento (assumindo que updateRecommendationFeedback foi ajustado ou espera boolean)
    const updated = await this.storage.updateRecommendationFeedback(recommendationId, liked);
    
    // Obter a recomendação atualizada
    // Presume-se que getRecommendationById retorna SharedRecommendation ou null
    const updatedRec = await this.storage.getRecommendationById(recommendationId);
    
    if (!updatedRec) {
       console.error('Erro ao buscar recomendação atualizada após feedback');
       // Retornar a recomendação original (se passada) ou lançar erro
       if (recommendation) return recommendation; 
       throw new Error('Falha ao obter recomendação atualizada.');
    }

    // Remover conversão - Retornar SharedRecommendation diretamente
    return updatedRec; // Retorna o tipo já esperado (SharedRecommendation)
  }
  
  /**
   * Rastreia cliques em links de afiliados
   */
  public async trackAffiliateClick(
    storeProductId: string, 
    userId?: string, 
    recommendationId?: string
  ): Promise<void> {
    // Reverter para a lógica anterior que usava o monitor para rastrear o clique
    this.monitor.trackClick({ 
      id: recommendationId || uuidv4(), // Usar ID se disponível, ou gerar um novo
      user_id: userId || '',
      game_id: '', // game_id não está disponível aqui, talvez precise ser buscado ou omitido
      score: 0, // score não está disponível aqui
      algorithm: RecommendationAlgorithm.POPULAR, // Algoritmo padrão/desconhecido aqui
      viewed: true,
      liked: null, // Clique não implica like/dislike
      game: undefined // game não está disponível aqui
    });
  }
  
  /**
   * Obtém recomendações que receberam feedback (curtidas ou não)
   * Refatorado para buscar diretamente do Supabase, similar à Facade
   */
  public async getFeedbackRecommendations(
    userId: string, 
    liked: boolean
  ): Promise<SharedRecommendation[]> {
    // Remover chamada ao storage, buscar diretamente
    // const feedbackRecs = await this.storage.getFeedbackRecommendations(userId, liked);
    try {
      const { data, error } = await this.supabase
        .from('recommendations')
        .select('*, game:games(*)') // Busca o jogo associado
        .eq('user_id', userId)
        .eq('liked', liked)
        .order('updated_at', { ascending: false });
        
      if (error) {
        console.error('[AdaptiveRecommender] Erro ao buscar recomendações com feedback:', error);
        return [];
      }

      // Mapear para SharedRecommendation, garantindo que 'game' está no formato correto
      const mappedData = (data || []).map((item: any) => {
        // Assegurar que item.game seja um objeto SharedGame ou undefined
        const gameData = item.game && typeof item.game === 'object' && !Array.isArray(item.game)
          ? item.game as SharedGame
          : undefined;

        return {
          id: item.id,
          user_id: item.user_id,
          game_id: item.game_id,
          game: gameData, // game é opcional (SharedGame | undefined)
          score: item.score,
          reason: item.reason,
          algorithm: item.algorithm as RecommendationAlgorithm,
          viewed: item.viewed,
          liked: item.liked,
          created_at: item.created_at,
          updated_at: item.updated_at
        };
      });

      // Filtrar itens nulos ou inválidos e fazer type assertion
      const recommendations = mappedData.filter(rec => rec !== null) as SharedRecommendation[];

      return recommendations;
    } catch (error) {
      console.error('[AdaptiveRecommender] Exceção ao buscar recomendações com feedback:', error);
      return [];
    }
  }
  
  /**
   * Remove o feedback de uma recomendação
   */
  public async removeFeedback(recommendationId: string): Promise<SharedRecommendation | null> {
    // Corrigir chamada para passar undefined em vez de null para o terceiro argumento
    return this.giveRecommendationFeedback(recommendationId, null, undefined);
  }
}